import { useEffect, useState } from 'react';
import { FUND_TYPES, CARES_GRANT_OPTIONS, STATES } from '../../../../constants/constants';
import { formatNumber } from '../../../../utils/formatters';
import { benefitService } from '../../../../services/benefitService';

/**
 * Centralized benefit tier calculation logic.
 * Shared by PPC / OLIBA, LIHEAP (WA), and CARES.
 *
 * Responsibilities:
 * - Decide WHEN a calculation should run
 * - Build the correct request payload
 * - Call benefitService.calculateTier
 * - Write calculated values back into form state
 */
export const useBenefitTierCalculation = ({
  form,
  formData,
  setFormField,
}) => {
  const [isTierCalculationPending, setIsTierCalculationPending] = useState(false);
  const fundType = form?.fundType;
  const state = formData?.state;

  /**
   * Small helper to run a debounced tier calculation.
   * Keeps each useEffect focused on business rules,
   * not timing / async boilerplate.
   */
  const runTierCalculation = (buildRequest, handleResponse) => {
    const request = buildRequest();
    if (!request) {
      setIsTierCalculationPending(false);
      return () => {};
    }

    setIsTierCalculationPending(true);
    const timeout = setTimeout(async () => {
      try {
        const response = await benefitService.calculateTier(request);
        if (!response?.success) {
          setIsTierCalculationPending(false);
          return;
        }

        handleResponse(response);
      } catch (err) {
        console.error('Tier calculation failed', err);
      } finally {
        setIsTierCalculationPending(false);
      }
    }, 500);

    return () => {
      clearTimeout(timeout);
      setIsTierCalculationPending(false);
    };
  };

  /* =========================================================
     PPC / OLIBA (percentage‑based)
     ========================================================= */
  useEffect(() => {
    return runTierCalculation(
      () => {
        if (fundType !== FUND_TYPES.PPC) return null;
        if (!form.incomeMeasurementType) return null;
        if (!form.incomeMeasurementPercentage) return null;

        return {
          fundType: FUND_TYPES.PPC,
          incomeMeasurementType: form.incomeMeasurementType,
          incomeMeasurementPercentage: parseInt(
            form.incomeMeasurementPercentage,
            10
          ),
        };
      },
      (response) => {
        // Guarded sets ensure idempotent effects and prevent re-fetch loops
        const normalizedEnergyDiscount = response.energyDiscount ?? '';
        const normalizedArrearage = response.arrearage ?? '';
        if (form.energyDiscount !== normalizedEnergyDiscount)
          setFormField('energyDiscount', normalizedEnergyDiscount);
        if (form.arrearage !== normalizedArrearage)
          setFormField('arrearage', normalizedArrearage);
      }
    );
  }, [
    fundType,
    form.incomeMeasurementType,
    form.incomeMeasurementPercentage,
    setFormField,
  ]);

  /* =========================================================
     LIHEAP (WA only – income‑based)
     ========================================================= */
  useEffect(() => {
    return runTierCalculation(
      () => {
        if (fundType !== FUND_TYPES.LIHEAP) return null;
        if (state !== STATES.WA) return null;
        if (!form.householdSize) return null;
        if (!form.income) return null;

        return {
          fundType: FUND_TYPES.LIHEAP,
          householdSize: parseInt(form.householdSize, 10),
          monthlyAdjustedIncome: parseFloat(form.income),
        };
      },
      (response) => {
        // Guarded sets ensure idempotent effects and prevent re-fetch loops
        const normalizedEnergyDiscount = response.energyDiscount ?? '';
        const normalizedArrearage = response.arrearage ?? '';
        const normalizedIncomeMeasurementType = response.incomeMeasurementType ?? '';
        const normalizedIncomeMeasurementPercentage = formatNumber(response.incomeMeasurementPercentage, 2) ?? '';
        if (form.energyDiscount !== normalizedEnergyDiscount)
          setFormField('energyDiscount', normalizedEnergyDiscount);
        if (form.arrearage !== normalizedArrearage)
          setFormField('arrearage', normalizedArrearage);
        if (form.incomeMeasurementType !== normalizedIncomeMeasurementType)
          setFormField('incomeMeasurementType', normalizedIncomeMeasurementType);
        if (form.incomeMeasurementPercentage !== normalizedIncomeMeasurementPercentage)
          setFormField('incomeMeasurementPercentage', normalizedIncomeMeasurementPercentage);
      }
    );
  }, [
    fundType,
    state,
    form.householdSize,
    form.income,
    setFormField
  ]);

  /* =========================================================
     CARES (WA only – income‑based, optional arrears liability)
     ========================================================= */
  useEffect(() => {
    return runTierCalculation(
      () => {
        if (fundType !== FUND_TYPES.CARES) return null;
        if (state !== STATES.WA) return null;
        if (!form.householdSize) return null;
        if (!form.income) return null;
        if (CARES_GRANT_OPTIONS.includes(form.caresType) && !form.arrearsLiabilityAmount) return null;

        return {
          fundType: FUND_TYPES.CARES,
          householdSize: parseInt(form.householdSize, 10),
          monthlyAdjustedIncome: parseFloat(form.income),
          arrearsLiabilityAmount: form.arrearsLiabilityAmount
            ? parseFloat(form.arrearsLiabilityAmount)
            : null,
        };
      },
      (response) => {
        // Guarded sets ensure idempotent effects and prevent re-fetch loops
        const normalizedEnergyDiscount = response.energyDiscount ?? '';
        const normalizedArrearage = response.arrearage ?? '';
        const normalizedIncomeMeasurementType = response.incomeMeasurementType ?? '';
        const normalizedIncomeMeasurementPercentage = formatNumber(response.incomeMeasurementPercentage, 2) ?? '';
        const normalizedPledgeAmount = formatNumber(response.calculatedPledgeAmount) ?? '';
        if (form.energyDiscount !== normalizedEnergyDiscount)
          setFormField('energyDiscount', normalizedEnergyDiscount);
        if (form.arrearage !== normalizedArrearage)
          setFormField('arrearage', normalizedArrearage);
        if (form.incomeMeasurementType !== normalizedIncomeMeasurementType)
          setFormField('incomeMeasurementType', normalizedIncomeMeasurementType);
        if (form.incomeMeasurementPercentage !== normalizedIncomeMeasurementPercentage)
          setFormField('incomeMeasurementPercentage', normalizedIncomeMeasurementPercentage);
        if (form.pledgeAmount !== normalizedPledgeAmount)
          setFormField('pledgeAmount', normalizedPledgeAmount);
      }
    );
  }, [
    fundType,
    state,
    form.householdSize,
    form.income,
    form.arrearsLiabilityAmount,
    form.caresType,
    setFormField
  ]);

  return { isTierCalculationPending };
};